import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { marked } from 'marked';
import { businessNumberLabel, businessNumberTypeForCountry, isValidAbn, spaceAbnInput } from '../businessNumber';
import { COUNTRIES } from '../countries';
import { missingPart } from '../addressFormat';
import PublicAddressFields, { EMPTY_PUBLIC_ADDRESS, changeCountry } from '../PublicAddressFields';
import Field from './SiteField';

// A Trading or Credit Account Application's private link (issue #290):
// /apply?token=..., emailed by staff from the business's record. The form
// opens pre-filled with what the business already told us, with the terms
// beside it. One authorised Director / Proprietor signs for the business by
// ticking to agree to the version shown and typing their full name. A
// second Director / Proprietor's details can be given; they don't sign.
// GET/POST /api/site/applications/:token (accountApplicationService.js).

// The signer's Role when they're the Primary Contact, from the Entity Type.
const ROLE_FOR_ENTITY_TYPE = { Company: 'Director', 'Sole Trader': 'Proprietor', Partnership: 'Partner', Trust: 'Trustee' };

const EMPTY_PERSON = { role: '', first_name: '', family_name: '', phone: '', email: '' };

// Any country (#395). Its Business Number type follows the country: ABN and
// NZBN are required, any other is optional.
const REQUIRED_NUMBER_TYPES = ['abn', 'nzbn'];

// The Business Number Optim holds, when it's the type the country asks for.
function heldNumber(held, country) {
  const b = held?.business || {};
  return b.business_number && b.business_number_type === businessNumberTypeForCountry(country) ? b.business_number : '';
}

function heldBusiness(held) {
  const b = held?.business || {};
  const country = b.country || 'Australia';
  return { country, name: b.name || '', business_number: heldNumber(held, country) };
}

// The site's own field, with an amber note under it for a part that needs
// a second look.
function AddressField({ id, label, warning, children }) {
  return (
    <Field label={label} htmlFor={id}>
      {children}
      {warning && <p className="ks-field-warning">{warning}</p>}
    </Field>
  );
}

// The pre-fill as the form's starting answers.
function startingAnswers(kind, held) {
  if (kind === 'credit') {
    return {
      business: heldBusiness(held),
      directors: [{ ...EMPTY_PERSON, ...(held?.directors?.[0] || {}) }],
    };
  }
  return {
    business: { ...heldBusiness(held), entity_type: '', address: { ...EMPTY_PUBLIC_ADDRESS } },
    about: { website: '', linkedin: '', instagram: '', facebook: '', physical_shop: '', brands: [], other_products: '', yearly_purchasing: '' },
    primary_contact: { ...EMPTY_PERSON, ...(held?.primary_contact || {}) },
    directors: [{ ...EMPTY_PERSON }, { ...EMPTY_PERSON }],
  };
}

// The terms' own Version Change Control table is the business's record,
// not the applicant's (same as the portal's Terms page). Terminology reads
// as a glossary: each "**Term** means ..." paragraph becomes the term over
// its definition.
function termsHtml(markdown) {
  const cut = String(markdown || '').split(/^## +Version Change Control\s*$/m)[0];
  const [body, terminology] = cut.split(/^## +Terminology\s*$/m);
  if (terminology === undefined) return marked.parse(cut);
  const entries = terminology.split(/\n\s*\n/).map((para) => {
    const m = para.trim().match(/^\*\*(.+?)\*\*\s+(.*)$/s);
    return m ? `<div><dt>${marked.parseInline(m[1])}</dt><dd>${marked.parseInline(m[2])}</dd></div>` : '';
  }).join('');
  return `${marked.parse(body)}<h2>Terminology</h2><dl class="ks-apply-glossary">${entries}</dl>`;
}

function Message({ eyebrow, title, children }) {
  return (
    <section className="ks-section">
      <div className="ks-wrap" style={{ maxWidth: 720 }}>
        <p className="ks-eyebrow">{eyebrow}</p>
        <h1 className="ks-display">{title}</h1>
        <p className="ks-lead">{children}</p>
      </div>
    </section>
  );
}

function Contact({ supplier }) {
  const parts = [];
  if (supplier?.email) parts.push(<a key="e" href={`mailto:${supplier.email}`}>{supplier.email}</a>);
  if (supplier?.phone) parts.push(<span key="p">{supplier.phone}</span>);
  if (!parts.length) return null;
  return <> at {parts.reduce((acc, el, i) => (i ? [...acc, ' or ', el] : [el]), [])}</>;
}

function PersonFields({ prefix, person, onChange, roles, roleLabel = 'Role', jobTitle = false, required = true }) {
  const set = (key) => (e) => onChange({ ...person, [key]: e.target.value });
  return (
    <>
      <Field label={roleLabel} htmlFor={`${prefix}-role`}>
        {jobTitle ? (
          <input id={`${prefix}-role`} value={person.role} onChange={set('role')} maxLength={100} required={required} autoComplete="organization-title" />
        ) : (
          <select id={`${prefix}-role`} value={person.role} onChange={set('role')} required={required}>
            <option value="">Choose…</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
      </Field>
      <div className="ks-form-row">
        <Field label="First Name" htmlFor={`${prefix}-first`}>
          <input id={`${prefix}-first`} value={person.first_name} onChange={set('first_name')} maxLength={100} required={required} />
        </Field>
        <Field label="Family Name" htmlFor={`${prefix}-family`}>
          <input id={`${prefix}-family`} value={person.family_name} onChange={set('family_name')} maxLength={100} required={required} />
        </Field>
      </div>
      <div className="ks-form-row">
        <Field label="Contact Phone" htmlFor={`${prefix}-phone`}>
          <input id={`${prefix}-phone`} type="tel" value={person.phone} onChange={set('phone')} maxLength={30} required={required} />
        </Field>
        <Field label="Contact Email" htmlFor={`${prefix}-email`}>
          <input id={`${prefix}-email`} type="email" value={person.email} onChange={set('email')} maxLength={255} required={required} />
        </Field>
      </div>
    </>
  );
}

function ApplicationPage() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const [view, setView] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState('');
  // Most often the Primary Contact is the one signing.
  const [sameAsContact, setSameAsContact] = useState(true);
  const [addSecond, setAddSecond] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(''); // the signer's email, once sent

  // A private link: keep it out of search results. Also loads the
  // handwriting font the typed signature shows in (.ks-apply-signature).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    const font = document.createElement('link');
    font.rel = 'stylesheet';
    font.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@500&display=swap';
    document.head.append(meta, font);
    return () => { meta.remove(); font.remove(); };
  }, []);

  useEffect(() => {
    axios.get(`/api/site/applications/${encodeURIComponent(token)}`)
      .then((res) => {
        const data = res.data.data;
        setView(data);
        if (data.state === 'open') setAnswers(startingAnswers(data.kind, data.form));
      })
      .catch((err) => setView({ state: 'error', message: err.response?.data?.message }));
  }, [token]);

  if (!view) return <Message eyebrow="Application" title="Loading…" />;
  if (view.state === 'error') return <Message eyebrow="Application" title="Something went wrong.">{view.message || 'Please try the link again in a moment.'}</Message>;
  if (done) {
    return (
      <Message eyebrow={view.label} title="Thanks, it's signed and sent.">
        We&apos;ve received your application and will be in touch once we&apos;ve reviewed it. A confirmation is on its way to {done}.
      </Message>
    );
  }
  if (view.state !== 'open') {
    const title = { expired: 'This link has expired.', cancelled: 'This link has been cancelled.', done: 'This link has already been used.' }[view.state];
    return (
      <Message eyebrow="Application" title={title}>
        Please contact {view.supplier?.name || 'us'}<Contact supplier={view.supplier} /> for a fresh link.
      </Message>
    );
  }

  const isCredit = view.kind === 'credit';
  const { terms } = view;
  const choices = { ...view.choices, brands: view.choices?.brands || [] };
  const termsTitle = isCredit ? 'Credit Terms' : 'Terms & Conditions of Trade';
  const setSection = (section) => (value) => setAnswers((prev) => ({ ...prev, [section]: value }));
  const country = answers.business.country;
  const numberType = businessNumberTypeForCountry(country);
  const numberRequired = REQUIRED_NUMBER_TYPES.includes(numberType);
  const setBusiness = (key) => (e) => setAnswers((prev) => ({ ...prev, business: { ...prev.business, [key]: e.target.value } }));
  const setBusinessNumber = (e) => setAnswers((prev) => ({ ...prev, business: { ...prev.business, business_number: numberType === 'abn' ? spaceAbnInput(e.target.value) : e.target.value } }));
  // A new country brings its own Business Number type, and lays the
  // Registered Address out its own way, keeping the parts both share.
  const setCountry = (e) => {
    const next = e.target.value;
    setAnswers((prev) => ({
      ...prev,
      business: {
        ...prev.business,
        country: next,
        business_number: businessNumberTypeForCountry(next) === numberType ? prev.business.business_number : heldNumber(view.form, next),
        ...(prev.business.address ? { address: changeCountry(prev.business.address, next) } : {}),
      },
    }));
  };
  const setAddress = (address) => setAnswers((prev) => ({ ...prev, business: { ...prev.business, address } }));
  // Australia Post's Suburbs for an Australian Registered Address's
  // Postcode, as it's typed (#158).
  const fetchPostcodeCheck = async (query) => {
    const res = await axios.get(`/api/site/applications/${encodeURIComponent(token)}/postcode-check`, { params: query });
    return res.data.result;
  };
  const setAbout = (key) => (e) => setAnswers((prev) => ({ ...prev, about: { ...prev.about, [key]: e.target.value } }));
  const toggleBrand = (brand) => (e) => setAnswers((prev) => {
    const brands = prev.about.brands.filter((b) => b !== brand);
    return { ...prev, about: { ...prev.about, brands: e.target.checked ? [...brands, brand] : brands } };
  });
  const setDirector = (index) => (value) => setAnswers((prev) => {
    const directors = [...prev.directors];
    directors[index] = value;
    return { ...prev, directors };
  });
  // The signing Director / Proprietor: when ticked Same as Primary Contact,
  // the Primary Contact's name, phone and email, with the Role that goes
  // with the Entity Type.
  const signingDirector = () => {
    const director = answers.directors[0];
    if (isCredit || !sameAsContact) return director;
    const { first_name: first, family_name: family, phone, email } = answers.primary_contact;
    return { role: ROLE_FOR_ENTITY_TYPE[answers.business.entity_type] || '', first_name: first, family_name: family, phone, email };
  };
  const signer = signingDirector();

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!isCredit && !answers.about.brands.length && !answers.about.other_products.trim()) {
      setError('Tick the brands you’re interested in, or describe other products.');
      return;
    }
    if (numberType === 'abn' && !isValidAbn(answers.business.business_number)) {
      setError("That ABN doesn't look right. Please check it.");
      return;
    }
    const missing = !isCredit && missingPart({ ...answers.business.address, country });
    if (missing) {
      setError(`Enter the Registered Address ${missing}.`);
      return;
    }
    setSending(true);
    try {
      const sent = { ...answers, directors: !isCredit && addSecond ? [signer, answers.directors[1]] : [signer] };
      await axios.post(`/api/site/applications/${encodeURIComponent(token)}/submit`, { answers: sent, signature: { agreed, name: signedName } });
      setDone(signer.email);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.message || "Sorry, we couldn't send your application. Please try again.");
    }
    setSending(false);
  };

  return (
    <section className="ks-section ks-section-tight">
      <div className="ks-wrap">
        <p className="ks-eyebrow">{view.supplier?.name}</p>
        <h1 className="ks-display">{view.label}</h1>
        <p className="ks-apply-intro">
          {isCredit
            ? 'Check your details, read the Credit Terms, then sign below.'
            : 'Fill in your business details, read the Terms & Conditions of Trade, then sign below. Fields are pre-filled from what you told us; change anything that isn’t right.'}
        </p>

        <div className="ks-apply">
          <form className="ks-form ks-apply-form" onSubmit={submit}>
            <h2 className="ks-apply-heading">{isCredit ? 'Customer Details' : 'The Customer'}</h2>
            <Field label="Country" htmlFor="ap-country">
              <select id="ap-country" value={country} onChange={setCountry} required>
                {!COUNTRIES.includes(country) && <option value={country}>{country}</option>}
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Business Name" htmlFor="ap-name">
              <input id="ap-name" value={answers.business.name} onChange={setBusiness('name')} maxLength={150} required autoComplete="organization" />
            </Field>
            <Field label={`Business ${businessNumberLabel(numberType)}${numberRequired ? '' : ' (optional)'}`} htmlFor="ap-number">
              <input
                id="ap-number"
                value={answers.business.business_number}
                onChange={setBusinessNumber}
                maxLength={numberType === 'abn' ? 14 : 30}
                required={numberRequired}
                inputMode={numberRequired ? 'numeric' : undefined}
                placeholder={numberType === 'abn' ? 'e.g. 12 345 678 901' : undefined}
              />
            </Field>
            {!isCredit && (
              <>
                <Field label="Entity Type" htmlFor="ap-entity">
                  <select id="ap-entity" value={answers.business.entity_type} onChange={setBusiness('entity_type')} required>
                    <option value="">Choose…</option>
                    {choices.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <h2 className="ks-apply-heading">Registered Address</h2>
                <PublicAddressFields
                  idPrefix="ap-address"
                  address={{ ...answers.business.address, country }}
                  onChange={setAddress}
                  Field={AddressField}
                  fetchCheck={fetchPostcodeCheck}
                />

                <h2 className="ks-apply-heading">About the Business</h2>
                <p className="ks-field-hint">The more you tell us, the faster we can open your account.</p>
                <div className="ks-form-row">
                  <Field label="Website (optional)" htmlFor="ap-website">
                    <input id="ap-website" value={answers.about.website} onChange={setAbout('website')} maxLength={255} autoComplete="url" />
                  </Field>
                  <Field label="LinkedIn (optional)" htmlFor="ap-linkedin">
                    <input id="ap-linkedin" value={answers.about.linkedin} onChange={setAbout('linkedin')} maxLength={255} />
                  </Field>
                </div>
                <div className="ks-form-row">
                  <Field label="Instagram (optional)" htmlFor="ap-instagram">
                    <input id="ap-instagram" value={answers.about.instagram} onChange={setAbout('instagram')} maxLength={255} />
                  </Field>
                  <Field label="Facebook (optional)" htmlFor="ap-facebook">
                    <input id="ap-facebook" value={answers.about.facebook} onChange={setAbout('facebook')} maxLength={255} />
                  </Field>
                </div>
                <div className="ks-form-row">
                  <Field label="Physical shop" htmlFor="ap-shop">
                    <select id="ap-shop" value={answers.about.physical_shop} onChange={setAbout('physical_shop')} required>
                      <option value="">Choose…</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </Field>
                  <Field label="Expected yearly purchasing" htmlFor="ap-purchasing">
                    <select id="ap-purchasing" value={answers.about.yearly_purchasing} onChange={setAbout('yearly_purchasing')} required>
                      <option value="">Choose…</option>
                      {choices.yearly_purchasing.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                </div>
                {choices.brands.length > 0 && (
                  <fieldset className="ks-apply-brands">
                    <legend>Brands of interest</legend>
                    <div className="ks-apply-brand-grid">
                      {choices.brands.map((brand) => (
                        <label key={brand} className="ks-apply-agree">
                          <input type="checkbox" checked={answers.about.brands.includes(brand)} onChange={toggleBrand(brand)} />
                          <span>{brand}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                <Field label={choices.brands.length ? 'Other products (optional)' : 'Brands or products of interest'} htmlFor="ap-other" hint={choices.brands.length ? 'Tick at least one brand, or tell us what else you’re after.' : undefined}>
                  <textarea id="ap-other" value={answers.about.other_products} onChange={setAbout('other_products')} maxLength={1000} rows={2} required={!choices.brands.length} />
                </Field>

                <h2 className="ks-apply-heading">Primary Contact</h2>
                <p className="ks-field-hint">The primary contact for orders, invoicing and accounts.</p>
                <PersonFields prefix="ap-pc" person={answers.primary_contact} onChange={setSection('primary_contact')} roleLabel="Job Title / Role" jobTitle />
              </>
            )}

            <h2 className="ks-apply-heading">Director / Proprietor</h2>
            <p className="ks-field-hint">
              {isCredit ? 'The Director / Proprietor signing this application.' : 'The authorised Director / Proprietor signing for the business. May be the same person as the Primary Contact.'}
            </p>
            {!isCredit && (
              <label className="ks-apply-agree">
                <input type="checkbox" checked={sameAsContact} onChange={(e) => setSameAsContact(e.target.checked)} />
                <span>Same as Primary Contact</span>
              </label>
            )}
            {(isCredit || !sameAsContact) && (
              <PersonFields prefix="ap-d1" person={answers.directors[0]} onChange={setDirector(0)} roles={choices.roles} />
            )}

            {!isCredit && !addSecond && (
              <div>
                <button type="button" className="ks-btn ks-btn-small" onClick={() => setAddSecond(true)}>Add a second Director / Proprietor</button>
              </div>
            )}
            {!isCredit && addSecond && (
              <>
                <h2 className="ks-apply-heading">Second Director / Proprietor</h2>
                <p className="ks-field-hint">
                  They don&apos;t need to sign.{' '}
                  <button type="button" className="ks-apply-link" onClick={() => setAddSecond(false)}>Remove</button>
                </p>
                <PersonFields prefix="ap-d2" person={answers.directors[1]} onChange={setDirector(1)} roles={choices.roles} />
              </>
            )}

            <h2 className="ks-apply-heading">Sign</h2>
            <div className="ks-apply-declaration">
              <p>By signing, I, as {signer.role ? `a ${signer.role}` : 'an authorised Director / Proprietor'} of the business:</p>
              <ul>
                {isCredit ? (
                  <li>understand and agree to the {termsTitle}, version {terms.version}, shown with this application;</li>
                ) : (
                  <>
                    <li>have read, understand and agree to the {termsTitle}, version {terms.version}, shown with this application;</li>
                    <li>authorise the Supplier to submit Orders on the business&apos;s behalf as set out in those Terms &amp; Conditions;</li>
                  </>
                )}
                <li>agree to notify the Supplier of any change to the information provided above;</li>
                <li>warrant that the individuals listed above are authorised representatives of the business;</li>
                <li>declare that all information is correct and complete (and not misleading, including by omission).</li>
              </ul>
            </div>
            <label className="ks-apply-agree">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
              <span>I agree to the {termsTitle}, version {terms.version}.</span>
            </label>
            <Field label="Type your full name to sign" htmlFor="ap-sign" hint="Your typed name is your signature. We record it with the date and time.">
              <input id="ap-sign" className="ks-apply-signature" value={signedName} onChange={(e) => setSignedName(e.target.value)} maxLength={150} required autoComplete="name" />
            </Field>

            {error && <p className="ks-form-error" role="alert">{error}</p>}
            <div className="ks-btn-row" style={{ marginTop: 8 }}>
              <button type="submit" className="ks-btn ks-btn-solid" disabled={sending}>
                {sending ? 'Sending…' : 'Sign and send'}
              </button>
            </div>
          </form>

          <aside className="ks-apply-terms" aria-label={termsTitle}>
            <h2 className="ks-apply-heading">{termsTitle}</h2>
            <p className="ks-legal-meta">Version {terms.version} · last updated {terms.updated}</p>
            <div className="ks-legal ks-apply-terms-body" dangerouslySetInnerHTML={{ __html: termsHtml(terms.markdown) }} />
          </aside>
        </div>
      </div>
    </section>
  );
}

export default ApplicationPage;
